import type { SectionCoverageRow } from './api-types';
import {
  sectionGapClosestHeading,
  sectionGapClosestPct,
  sectionGapGroupMembers,
  sectionGapHasCloseMatch,
  sectionGapLabel,
  sectionGapLevel,
  type SectionGapMember
} from './section-gaps';
import { extractFirstSentence } from './keyword-passage-evidence';
import { alreadyOnPageQuote, alreadyOnPageQuoteLine, type AlreadyOnPageQuote, type OwnPagePassage } from './already-on-page';

/**
 * "Missing sections", as a decision rather than a list of headings.
 *
 * The card used to print the rival's heading and stop. That names the finding
 * and answers none of what an operator is weighing when they decide whether to
 * send it: a heading is a label, not a size, and two sections with equally
 * plausible headings can be a morning's work or a rewrite of something that
 * already exists.
 *
 * Two things go under each heading.
 *
 * **Which kind of work it is.** `sectionCovered` is the analyzer's own verdict,
 * and it splits the list exactly where the work splits:
 *
 * - **not covered** → nothing of yours matched above the threshold. A section
 *   to write.
 * - **covered, but with absent terms** → you address it and miss its
 *   vocabulary. A section to deepen. `utils/section-gaps` keeps these in the
 *   list precisely because they are real gaps, and until now they were
 *   indistinguishable from the other kind.
 *
 * A near-miss is deliberately NOT called thin. A row at 39% similarity was
 * judged not covered, and "you have this already" over a page that does not is
 * the more expensive error — it sends a writer to expand a section they then
 * cannot find. The nearest heading is shown for the covered kind only, where it
 * is a pointer to the paragraph to open.
 *
 * **What they actually say.** The opening sentence of their section.
 *
 * A sentence is always there when the section is, it is evidence rather than a
 * word list, and it answers the question a heading provokes — "what do they
 * actually put in it?" — in one line.
 *
 * **What the group collapsed, and their vocabulary.** Both were once cut from
 * this card and are back for the same reason: a heading names a finding and
 * describes nothing, and an operator deciding whether to send it should not
 * have to open Compare to learn what the section is about.
 *
 * The old objection to the absent terms still stands on its own terms — they
 * come from ONE section of ONE rival, so a long section yields "mobile app
 * development, post-launch customer support" and a short one with a heading our
 * crawl mangled yields "cheat shee". What changed is that they are no longer
 * the only line under the heading. With the opening sentence above them and,
 * on a grouped gap, the sibling sections it collapsed, a weak term row degrades
 * instead of leaving the operator with nothing.
 *
 * The page-level vocabulary gap remains a different measurement on a different
 * card — the terms the whole SERP shares, from every top result.
 *
 */

/** One line on a belt. Long enough to be evidence, short enough not to be a body. */
const MAX_EXCERPT = 150;

/** Enough terms to recognise the subject; past this it reads as a dump. */
const MAX_ABSENT_TERMS = 8;

export interface ReviewMissingSectionRow {
  /** Stable within a card. Section number where the analyzer gave one. */
  id: string;
  label: string;
  /** `write` — nothing of yours matched. `deepen` — you cover it thinly. */
  kind: 'write' | 'deepen';
  /** The paragraph to open, on `deepen` rows only. Null when we cannot name one. */
  closest: { heading: string; pct: number } | null;
  /** The opening sentence of their section. Null when it has no body text. */
  excerpt: string | null;
  /**
   * The sibling sections this row collapsed, when it collapsed more than one.
   *
   * A gap of three examples under one parent heading is one decision but three
   * pieces of writing, and the label alone ("Project scope examples") hides
   * both the size and the shape of it. Empty on an ordinary row.
   */
  members: SectionGapMember[];
  /** Heading level on the rival's page, so a section reads as a section. */
  level: number | null;
  /**
   * Terms their section uses that your page does not — what makes it a gap
   * rather than a heading you happen not to have.
   */
  absentTerms: string[];
}

export interface ReviewMissingSectionsView {
  /** The whole sentence, for anywhere that cannot render the link. */
  verdict: string;
  /** The rival's name — the half of the sentence that becomes the link. */
  rivalLabel: string;
  /** The rest of the sentence, so the link stops at the name. */
  verdictRest: string;
  /**
   * The rival PAGE this comparison ran against.
   *
   * Null when we cannot name one, and never a bare domain: an operator who
   * clicks "business.adobe.com" here is asking to see the ARTICLE that
   * outranks them, and a homepage makes them hunt for it.
   */
  rivalUrl: string | null;
  rows: ReviewMissingSectionRow[];
  /**
   * Their sections our page already has, each with the line of ours that shows it (TECHNICAL_DEBT
   * §0cb). Not rows, and not rendered on /review: nothing to write. Carried for the everything-mode
   * export, where a grader can check the claim.
   */
  alreadyOnPage: ReviewAlreadyOnPageSection[];
}

/** A rival section left off the list because our page already has it. */
export interface ReviewAlreadyOnPageSection {
  label: string;
  level: number | null;
  quote: AlreadyOnPageQuote;
  /** The quote in one line, as the card and the export print it. */
  quoteLine: string;
}

/**
 * The gap rows split into those our page does not cover and those it already has, with the quote.
 * The heading tested is the one the row is labelled with (a grouped row's parent), untruncated:
 * that is the section the item would ask for.
 */
export function splitSectionGapsByOwnPage(
  gaps: readonly SectionCoverageRow[] | null | undefined,
  ownPassages: readonly OwnPagePassage[] | null | undefined
): { open: SectionCoverageRow[]; onPage: ReviewAlreadyOnPageSection[] } {
  const open: SectionCoverageRow[] = [];
  const onPage: ReviewAlreadyOnPageSection[] = [];
  for (const sc of gaps ?? []) {
    const quote = ownPassages?.length ? alreadyOnPageQuote(sectionGapLabel(sc, Infinity), ownPassages) : null;
    if (quote) onPage.push({ label: sectionGapLabel(sc), level: sectionGapLevel(sc), quote, quoteLine: alreadyOnPageQuoteLine(quote) });
    else open.push(sc);
  }
  return { open, onPage };
}

/**
 * The section's own text, with any leading line that is only a heading removed.
 *
 * A grouped row's body starts at its first member, and that member's heading is
 * the first thing in it — so the excerpt for Adobe's "Project scope examples"
 * read “Website development project.”, which is a heading the operator can
 * already see two lines above, restated as if it were evidence. Dropping the
 * repeat gets the sentence that actually describes the section: "Objectives:
 * Increase website traffic by 20%…".
 *
 * Only leading repeats go. A heading echoed mid-section is the rival writing,
 * and cutting there would take real prose with it.
 */
function bodyPastHeadings(text: string, headings: readonly string[]): string {
  const known = new Set(
    headings.map((h) => h.replace(/[.\s]+$/, '').replace(/\s+/g, ' ').trim().toLowerCase()).filter(Boolean)
  );
  if (!known.size) return text || '';

  let rest = (text || '').replace(/\s+/g, ' ').trim();
  // Bounded rather than while(true): a body that is nothing but headings should
  // end up empty and render no excerpt, not spin.
  for (let i = 0; i < 6; i++) {
    const match = rest.match(/^(.{3,120}?[.!?:])(?:\s+|$)/);
    const head = match?.[1];
    if (!head) break;
    if (!known.has(head.replace(/[.:\s]+$/, '').trim().toLowerCase())) break;
    rest = rest.slice(match[0].length).trim();
  }
  return rest;
}

function rowFor(sc: SectionCoverageRow, index: number): ReviewMissingSectionRow {
  const label = sectionGapLabel(sc);
  const members = sectionGapGroupMembers(sc);
  const kind: 'write' | 'deepen' = sc.sectionCovered ? 'deepen' : 'write';
  const closest =
    kind === 'deepen' && sectionGapHasCloseMatch(sc)
      ? { heading: sectionGapClosestHeading(sc), pct: sectionGapClosestPct(sc) }
      : null;

  return {
    id: sc.sectionNumber != null ? `s${sc.sectionNumber}` : `i${index}`,
    label,
    kind,
    closest,
    excerpt: sectionExcerpt(
      bodyPastHeadings(sc.competitorText || '', [
        label,
        ...members.map((m) => m.heading),
        sc.competitorHeading || ''
      ])
    ),
    members,
    level: sectionGapLevel(sc),
    // Capped: this is a sense of what the section is about, not its index.
    absentTerms: (sc.absentTerms || [])
      .map((t) => (t || '').replace(/\s+/g, ' ').trim())
      .filter(Boolean)
      .slice(0, MAX_ABSENT_TERMS)
  };
}

/**
 * Truncation is on a word boundary and says so with an ellipsis.
 *
 * A sentence cut mid-word reads as corrupted data rather than as an excerpt,
 * and this card already carries one heading our crawl mangled.
 */
function sectionExcerpt(text: string | undefined): string | null {
  const sentence = extractFirstSentence(text || '').trim();
  if (!sentence) return null;
  if (sentence.length <= MAX_EXCERPT) return sentence;
  const cut = sentence.slice(0, MAX_EXCERPT);
  const lastSpace = cut.lastIndexOf(' ');
  return `${(lastSpace > 40 ? cut.slice(0, lastSpace) : cut).replace(/[.,;:\s]+$/, '')}…`;
}

export function reviewMissingSections(
  gaps: readonly SectionCoverageRow[] | null | undefined,
  domain: string,
  rivalUrl?: string | null,
  /** Our page's stored text (`ownPagePassages`); omitted, nothing is checked against it. */
  ownPassages?: readonly OwnPagePassage[] | null
): ReviewMissingSectionsView | null {
  const { open, onPage } = splitSectionGapsByOwnPage(gaps, ownPassages);
  const rows = open.map(rowFor);
  if (!rows.length) return null;

  const who = (domain || '').trim() || 'The top result';
  const verdictRest =
    rows.length === 1
      ? ' covers 1 section you don’t.'
      : ` covers ${rows.length} sections you don’t.`;

  // Only when it is a page. A bare domain as the href sends the operator to a
  // homepage to hunt for the article they were just told about.
  const href = (rivalUrl || '').trim();
  const linkable = /^https?:\/\//i.test(href) && !/^https?:\/\/[^/]+\/?$/i.test(href);

  return {
    verdict: `${who}${verdictRest}`,
    rivalLabel: who,
    verdictRest,
    rivalUrl: linkable ? href : null,
    rows,
    alreadyOnPage: onPage
  };
}
